import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Image } from "../../../common/Image";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { IProduct } from "../../../models/catalogue";
import { isoProductID } from "../../../models/nominals";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { DiscountDisplayMode } from "../constants";
import { IModularConfiguratorOptionSet } from "../models";

import styles from "./PanelRootProductSelectorOption.module.scss";

const getImage = (rootProduct: IProduct): IProduct["images"][0] | null => {
    if (rootProduct.images.length <= 0) {
        return null;
    }
    const image =
        rootProduct.images.length > 1
            ? rootProduct.images.find((img) => img.role === "PLP Config")
            : rootProduct.images[0];
    return image || null;
};

const getLowestPriceVariant = (rootProduct: IProduct): IProduct => {
    // get lowest priced variant that is available to buy
    const variant = rootProduct.children.reduce((prev, curr) => {
        if (
            !prev.availability.is_available_to_buy &&
            curr.availability.is_available_to_buy
        ) {
            return curr;
        }
        if (!curr.availability.is_available_to_buy) {
            return prev;
        }
        const currPrice = getProductPrice(curr.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        const prevPrice = getProductPrice(prev.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        return currPrice.lessThan(prevPrice) ? curr : prev;
    }, rootProduct.children[0]);
    return variant;
};

const areAllVariantsOutOfStock = (rootProduct: IProduct): boolean => {
    const firstVariantAvailability =
        rootProduct.children[0].availability.is_available_to_buy;
    if (firstVariantAvailability) {
        return false;
    } else {
        return rootProduct.children.every(function (element) {
            return (
                element.availability.is_available_to_buy ===
                firstVariantAvailability
            );
        });
    }
};

const buildAboveRatingContent = (rootProduct: IProduct): React.ReactNode => {
    if (rootProduct.attributes.configurator_panel_above_rating_text) {
        return (
            <RichText
                html={`${rootProduct.attributes.configurator_panel_above_rating_text.value}`}
            />
        );
    }
    if (rootProduct.description) {
        return <RichText html={rootProduct.description} />;
    }
    return null;
};

const buildBelowRatingContent = (rootProduct: IProduct): React.ReactNode => {
    if (rootProduct.attributes.configurator_panel_below_rating_text) {
        return (
            <RichText
                html={`${rootProduct.attributes.configurator_panel_below_rating_text.value}`}
            />
        );
    }
    if (
        rootProduct.attributes.title_richtext &&
        rootProduct.attributes.title_richtext.value
    ) {
        return (
            <RichText
                html={`${rootProduct.attributes.title_richtext.value}`}
                tagName="h2"
            />
        );
    }
    return null;
};

export const PanelRootProductSelectorOption = (props: {
    optionSet: IModularConfiguratorOptionSet;
    rootProduct: IProduct;
    selectedRootProduct: IProduct;
    onSelect: () => void;
    onClick: () => void;
    enableRatings: boolean;
}): React.ReactNode => {
    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        e.preventDefault();
        props.onSelect();
    };

    const handleClick = (
        e: React.FormEvent<HTMLInputElement> | React.MouseEvent<Element>,
    ) => {
        e.preventDefault();
        props.onClick();

        if (e.currentTarget.className !== "stars-rating-link") {
            return;
        }

        // Scroll and focus on the next tick since closing the modal will change focus also. We
        // need to make sure this happens after that.
        setTimeout(() => {
            const document = getDocument();
            const reviews = document?.querySelector<HTMLElement>(
                ".customer-review__button",
            );
            reviews?.scrollIntoView(true);
            reviews?.focus();
        }, 0);
    };

    const optionSelected =
        props.rootProduct.id === props.selectedRootProduct?.id;
    const optionClass = classNames({
        [styles.radioOption]: true,
        [styles.activeOption]: optionSelected,
    });
    const optionContainerClass = classNames({
        "configurator__radio-option-container": true,
        "configurator__radio-option-container--option-model": true,
        "configurator__radio-option-container--disable":
            areAllVariantsOutOfStock(props.rootProduct),
        [styles.optionContainer]: true,
    });
    const variant = getLowestPriceVariant(props.rootProduct);
    const image = getImage(props.rootProduct);

    return (
        <li>
            {props.rootProduct.attributes.tile_header &&
                props.rootProduct.attributes.tile_header.value && (
                    <RichText
                        className="rich-text configurator__radio-option-container--tile-header"
                        html={`${props.rootProduct.attributes.tile_header.value}`}
                        tagName="h3"
                    />
                )}
            <div className={optionContainerClass}>
                <div className={styles.ratingContainer}>
                    {buildAboveRatingContent(props.rootProduct)}
                    {props.rootProduct.rating && props.enableRatings && (
                        <button
                            onClick={handleClick}
                            className={styles.ratingButton}
                        >
                            <RatingGraphic
                                rating={props.rootProduct.rating}
                                numReviews={props.rootProduct.num_reviews}
                                onlyReviewsNum={true}
                                cardClass={"panel-model"}
                                cardSize={"small"}
                                starRatingClass={styles.ratingStars}
                                reviewNumberClass={styles.reviewNumber}
                            />
                        </button>
                    )}
                </div>
                <input
                    type="radio"
                    id={`${props.rootProduct.id}`}
                    name={"option_model"}
                    onChange={handleChange}
                    onClick={handleClick}
                    value={isoProductID.unwrap(props.rootProduct.id)}
                    checked={optionSelected}
                />
                <label
                    htmlFor={`${props.rootProduct.id}`}
                    className={optionClass}
                    aria-selected={optionSelected}
                >
                    <div className={styles.imgContainer}>
                        {image && (
                            <Image
                                src={image.original}
                                className="responsive-img"
                                alt={props.rootProduct.title}
                            />
                        )}
                    </div>
                    <div className={styles.contentContainer}>
                        {buildBelowRatingContent(props.rootProduct)}
                        <ConfiguratorProductPrice
                            configuratorType={props.optionSet.layout.design}
                            strikeThroughMSRP={false}
                            discountDisplayMode={DiscountDisplayMode.DISABLED}
                            actualPriceStyle={""}
                            price={variant.price}
                            pricingMessage={t`Starting at`}
                            calloutCopy={
                                props.rootProduct.attributes.product_callout
                                    ?.value
                            }
                            actualPriceClass={styles.priceActual}
                            messageClass={styles.priceMessage}
                            retailPriceClass={styles.priceRetail}
                        />
                    </div>
                </label>
            </div>
        </li>
    );
};
